import React, { useState } from "react";
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  CircularProgress,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuery, useMutation } from "@apollo/client";
import { GET_INBOX } from "../graphql/query";
import { MARK_NOTIFICATION_AS_READ, MARK_ALL_AS_READ } from "../graphql/mutation";
import { useNavigate } from "react-router-dom";
import themestyle from "../theme";
import { stripNotificationReference } from "../utils/notification";

const NotificationDropdown = ({ isPhoneLike }: { isPhoneLike: boolean }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery(GET_INBOX, {
    fetchPolicy: "cache-and-network",
    pollInterval: 60000,
  });

  const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ);
  const [markAllAsRead] = useMutation(MARK_ALL_AS_READ);

  const notifications = data?.getInbox?.data || [];
  const unreadNotifications = notifications.filter((n: any) => !n.isRead);
  const unreadCount = unreadNotifications.length;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notif: any) => {
    if (!notif.isRead) {
      await markAsRead({ variables: { id: notif.id } });
      refetch();
    }
    handleClose();
    navigate("/notifications");
  };

  const handleClearAll = async () => {
    try {
      await markAllAsRead();
      refetch();
    } catch (error) {
      console.error("Failed to clear all notifications:", error);
    }
  };

  const timeAgo = (dateParam: string | number) => {
    const date = new Date(Number(dateParam) || dateParam);
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };

    for (const [key, value] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / value);
      if (interval >= 1) {
        return interval === 1 ? `1 ${key} ago` : `${interval} ${key}s ago`;
      }
    }
    return "just now";
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          border: "1px solid #DDE6D5",
          borderRadius: "14px",
          width: isPhoneLike ? 36 : 44,
          height: isPhoneLike ? 36 : 44,
          "& svg": {
            fontSize: isPhoneLike ? 20 : 24,
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 500,
            borderRadius: "12px",
            mt: 1.5,
            boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
            overflow: "hidden",
            "& .MuiList-root": {
              p: 0,
            }
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E5E7EB", backgroundColor: "#FFFFFF", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Badge 
                badgeContent={unreadCount} 
                color="success" 
                sx={{ 
                  "& .MuiBadge-badge": { 
                    position: "static", 
                    transform: "none",
                    backgroundColor: "#F7FAF3",
                    color: themestyle.colors.activeGreen,
                    fontWeight: 700,
                    border: `1px solid ${themestyle.colors.activeGreen}`,
                    minWidth: "auto",
                    height: "20px",
                    px: 1
                  } 
                }}
              />
            )}
          </Box>
          <Button 
            onClick={handleClearAll}
            disabled={unreadCount === 0}
            sx={{ 
              textTransform: "none", 
              fontSize: "12px", 
              color: themestyle.colors.activeGreen,
              minWidth: "auto",
              p: 0,
              fontWeight: 600,
              "&:hover": { backgroundColor: "transparent", textDecoration: "underline" }
            }}
          >
            Clear All
          </Button>
        </Box>

        <Box 
          id="notification-scroll-area"
          sx={{ 
            overflowY: "auto", 
            flexGrow: 1, 
            minHeight: "100px", 
            maxHeight: "350px",
            backgroundColor: "#FFFFFF",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#E5E7EB", borderRadius: "10px" }
          }}
        >
          <List id="notification-list" sx={{ p: 0, width: "100%" }}>
            {loading && notifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : unreadNotifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            ) : (
              unreadNotifications.slice(0, 15).map((notif: any) => (
                <ListItem
                  key={notif.id}
                  disablePadding
                  sx={{
                    borderBottom: "1px solid #F3F4F6",
                    backgroundColor: "#F7FAF3",
                    "&:hover": { backgroundColor: "#F3F4F6" },
                  }}
                >
                  <ListItemButton onClick={() => handleNotificationClick(notif)}>
                    <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
                      {notif.title?.toLowerCase().includes("successful") || notif.title?.toLowerCase().includes("success") ? (
                        <CheckCircleIcon sx={{ color: "#4ADE80", fontSize: 16 }} />
                      ) : (
                        <CircleIcon sx={{ color: "#4ADE80", fontSize: 10 }} />
                      )}
                    </Box>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {notif.title}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {stripNotificationReference(notif.message)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {timeAgo(notif.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
        <Box sx={{ p: 1, borderTop: "1px solid #E5E7EB", backgroundColor: "#FFFFFF", zIndex: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              handleClose();
              navigate("/notifications");
            }}
            sx={{
              textTransform: "none",
              color: themestyle.colors.activeGreen,
              fontWeight: 600,
            }}
          >
            See all notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationDropdown;
